<template>
  <div class="dp-wrap" :class="wrapperClass" :style="wrapperStyle">
    <VueDatePicker
      :model-value="innerValue"
      :auto-apply="isMonthMode || !enableTime"
      :close-on-auto-apply="isMonthMode || !enableTime"
      :month-picker="isMonthMode"
      :enable-time-picker="!isMonthMode && enableTime"
      :enable-seconds="!isMonthMode && enableTime && enableSeconds"
      :format="displayFormat"
      :input-class-name="inputClass"
      :menu-class-name="popupClass"
      text-input
      v-bind="passThroughAttrs"
      @update:model-value="onUpdateFromPicker"
    >
      <template #dp-input="{ onInput, onEnter, onTab, onBlur, onKeypress, onPaste, openMenu }">
        <div class="dp-field" role="button" tabindex="0" @click="openMenu()">
          <input
            class="dp-input"
            :class="inputClass"
            :value="displayText"
            readonly
            aria-readonly="true"
            @input="onInput($event)"
            @keydown.enter.prevent="onEnter()"
            @keydown.tab="onTab()"
            @blur="onBlur()"
            @keypress="onKeypress($event)"
            @paste="onPaste($event)"
          />

          <button type="button" class="dp-icon" aria-label="Open calendar" tabindex="-1">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M7 3v2M17 3v2M4.5 8.5h15M6 6h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </template>
    </VueDatePicker>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, useAttrs, PropType } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export type DatePickerMode = 'date' | 'month';

function cloneDate(d: Date) {
  return new Date(d.getTime());
}

function toValidDate(v: any, formatter: any): Date | null {
  if (!v) return null;

  if (v instanceof Date) return Number.isFinite(v.valueOf()) ? v : null;

  // {month, year} object emitted by vue-datepicker's month-picker
  if (typeof v === 'object' && !Array.isArray(v) && typeof v.month === 'number' && typeof v.year === 'number') {
    const d = new Date(v.year, v.month, 1, 0, 0, 0, 0);
    return Number.isFinite(d.valueOf()) ? d : null;
  }

  if (Array.isArray(v)) return null;

  if (typeof v === 'string') {
    if (formatter?.parse) {
      const parsed = formatter.parse(v);
      if (parsed instanceof Date && Number.isFinite(parsed.valueOf())) return parsed;
    }

    const d = new Date(v);
    return Number.isFinite(d.valueOf()) ? d : null;
  }

  if (typeof v === 'number') {
    const d = new Date(v);
    return Number.isFinite(d.valueOf()) ? d : null;
  }

  try {
    const d = new Date(v);
    return Number.isFinite(d.valueOf()) ? d : null;
  } catch {
    return null;
  }
}

function formatMonthYear(d: Date) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${mm}/${d.getFullYear()}`;
}

export default defineComponent({
  name: 'DatePicker',
  compatConfig: { MODE: 3 },
  components: { VueDatePicker },
  inheritAttrs: false,

  props: {
    modelValue: { type: [Date, String, Number, Object] as any, default: null },
    formatter: { type: Object as any, default: null },
    inputClass: { type: String, default: '' },
    popupClass: { type: String, default: '' },
    enableTime: { type: Boolean, default: false },
    enableSeconds: { type: Boolean, default: false },
    mode: {
      type: String as PropType<DatePickerMode>,
      default: 'date',
      validator: (v: string) => ['date', 'month'].includes(v)
    }
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const attrs = useAttrs();

    const rawWrapperClass = computed(() => (attrs as any).class);
    const wrapperStyle = computed(() => (attrs as any).style);

    const isMonthMode = computed(() => props.mode === 'month');

    const passThroughAttrs = computed(() => {
      const a: any = { ...attrs };

      delete a.class;
      delete a.style;

      delete a.formatter;
      delete a.inputClass;
      delete a.popupClass;
      delete a['input-class'];
      delete a['popup-class'];

      delete a.enableTime;
      delete a['enable-time'];

      delete a.enableSeconds;
      delete a['enable-seconds'];

      delete a['enable-time-picker'];
      delete a.enableTimePicker;

      delete a.mode;
      delete a['month-picker'];
      delete a.monthPicker;

      return a;
    });

    const innerValue = computed(() => {
      const d = toValidDate(props.modelValue, props.formatter);
      if (!d) return null;

      // month-picker expects { month, year }
      if (isMonthMode.value) {
        return { month: d.getMonth(), year: d.getFullYear() };
      }

      // ใช้ค่าจริงตรง ๆ เพื่อไม่ให้เวลาใน popup กับ input เหลื่อมกัน
      return cloneDate(d);
    });

    const onUpdateFromPicker = (v: any) => {
      // month-picker emits { month, year } | null
      if (isMonthMode.value) {
        if (
          !v ||
          typeof v !== 'object' ||
          typeof (v as any).month !== 'number' ||
          typeof (v as any).year !== 'number'
        ) {
          emit('update:modelValue', null);
          return;
        }

        const normalized = new Date((v as any).year, (v as any).month, 1, 0, 0, 0, 0);
        emit('update:modelValue', normalized);
        return;
      }

      const d = toValidDate(v, props.formatter);

      if (!d) {
        emit('update:modelValue', null);
        return;
      }

      if (!props.enableTime) {
        const normalized = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
        emit('update:modelValue', normalized);
        return;
      }

      emit('update:modelValue', d);
    };

    const displayText = computed(() => {
      const d = toValidDate(props.modelValue, props.formatter);
      if (!d) return '';

      const f: any = props.formatter;
      if (f?.stringify) return f.stringify(d);

      if (isMonthMode.value) return formatMonthYear(d);

      return props.enableTime ? d.toLocaleString() : d.toLocaleDateString();
    });

    const displayFormat = (date: Date | { month: number; year: number }) => {
      const f: any = props.formatter;

      if (isMonthMode.value) {
        // vue-datepicker passes { month, year } in month-picker mode
        if (date && typeof (date as any).month === 'number' && typeof (date as any).year === 'number') {
          const o = date as { month: number; year: number };
          const mm = String(o.month + 1).padStart(2, '0');
          return `${mm}/${o.year}`;
        }

        if (date instanceof Date) return formatMonthYear(date);
        return '';
      }

      if (f?.stringify && date instanceof Date) return f.stringify(date);
      if (!(date instanceof Date)) return '';
      return props.enableTime ? date.toLocaleString() : date.toLocaleDateString();
    };

    const hideTimeClass = computed(() => (!isMonthMode.value && !props.enableTime ? 'dp-hide-time' : ''));

    const wrapperClass = computed(() => {
      const classes: Array<string | Record<string, boolean> | string[]> = [];

      if (rawWrapperClass.value) {
        classes.push(rawWrapperClass.value as any);
      }

      if (hideTimeClass.value) {
        classes.push(hideTimeClass.value);
      }

      if (isMonthMode.value) {
        classes.push('dp-month-mode');
      }

      return classes;
    });

    return {
      isMonthMode,
      innerValue,
      onUpdateFromPicker,
      displayFormat,
      displayText,
      wrapperClass,
      wrapperStyle,
      passThroughAttrs
    };
  }
});
</script>

<style scoped>
.dp-wrap {
  width: 100%;
}

.dp-field {
  position: relative;
  width: 100%;
}

.dp-input {
  width: 100%;
  box-sizing: border-box;
  padding-right: 2.5rem;
}

.dp-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: rgba(51, 51, 51, 0.7);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  pointer-events: none;
  padding: 0;
}

.dp-icon svg {
  width: 18px !important;
  height: 18px !important;
  display: block;
}

:deep(.dp-hide-time .dp__time_input),
:deep(.dp-hide-time .dp__time_col),
:deep(.dp-hide-time .dp__time_picker),
:deep(.dp-hide-time .dp__time),
:deep(.dp-hide-time .dp__time_display) {
  display: none !important;
}

/* hide day grid / time bits when in month-picker mode just in case */
:deep(.dp-month-mode .dp__time_input),
:deep(.dp-month-mode .dp__time_col),
:deep(.dp-month-mode .dp__time_picker),
:deep(.dp-month-mode .dp__time),
:deep(.dp-month-mode .dp__time_display) {
  display: none !important;
}

:deep(.dp--clear-btn) {
  display: none !important;
}

:deep(.dp__input_icons) {
  display: none !important;
}
</style>
